import React, { useEffect, useState, useCallback, useRef } from 'react'
import styled from 'styled-components'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import useDebounce from 'utils/hooks/useDebounce'
import { isEmpty } from 'ramda'
import { searchLocalFilesRoutine } from 'features/bpDetails/ducks/actions'
import { getFileTagsForBpRoutine } from 'ducks/files/actions'
import { useDispatch, useSelector } from 'react-redux'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { selectBpFilesList, selectFileTagsForBp, getFileTypes } from 'ducks/files/selectors'
import { CircularProgress, Tooltip, Chip, ToggleButtonGroup, ToggleButton, Collapse, IconButton } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FilterListIcon from '@mui/icons-material/FilterList'
import FileTypeMultiSelect from './files/FileTypeMultiSelect'
import UploaderMultiSelect from './files/UploaderMultiSelect'
import TagsMultiSelect from './files/TagsMultiSelect'
import ViewableBySelect from './files/ViewableBySelect'
import { getFileUploaders } from 'services/fileService'

export const StreamlinedFilesSearch = ({
  disabled = false,
  setIsSearching,
  onSearchCriteria = () => {},
  onChange = () => {},
  // Allow external control of filter states
  searchQuery: externalSearchQuery,
  setSearchQuery: externalSetSearchQuery,
  selectedUploaders: externalSelectedUploaders,
  setSelectedUploaders: externalSetSelectedUploaders,
  selectedFileTypes: externalSelectedFileTypes,
  setSelectedFileTypes: externalSetSelectedFileTypes,
  selectedFileTags: externalSelectedFileTags,
  setSelectedFileTags: externalSetSelectedFileTags,
  selectedViewableBy: externalSelectedViewableBy,
  setSelectedViewableBy: externalSetSelectedViewableBy,
  searchMode: externalSearchMode,
  setSearchMode: externalSetSearchMode,
  selectedFolder = null,
  selectedCategory = null,
  customViewId = null,
 ...props
}) => {
  // Use external states if provided, otherwise use internal states
  const [internalValue, setInternalValue] = useState('')
  const [internalSelectedUploaders, setInternalSelectedUploaders] = useState([])
  const [internalSelectedFileTypes, setInternalSelectedFileTypes] = useState([])
  const [internalSelectedFileTags, setInternalSelectedFileTags] = useState([])
  const [internalSelectedViewableBy, setInternalSelectedViewableBy] = useState([])
  const [internalSearchMode, setInternalSearchMode] = useState(() => {
    const saved = localStorage.getItem('fileSearchMode')
    return saved || 'name' // Default to 'name' mode
  })

  const value = externalSearchQuery !== undefined ? externalSearchQuery : internalValue
  const setValue = externalSetSearchQuery || setInternalValue
  const selectedUploaders = externalSelectedUploaders !== undefined ? externalSelectedUploaders : internalSelectedUploaders
  const setSelectedUploaders = externalSetSelectedUploaders || setInternalSelectedUploaders
  const selectedFileTypes = externalSelectedFileTypes !== undefined ? externalSelectedFileTypes : internalSelectedFileTypes
  const setSelectedFileTypes = externalSetSelectedFileTypes || setInternalSelectedFileTypes
  const selectedFileTags = externalSelectedFileTags !== undefined ? externalSelectedFileTags : internalSelectedFileTags
  const setSelectedFileTags = externalSetSelectedFileTags || setInternalSelectedFileTags
  const selectedViewableBy = externalSelectedViewableBy !== undefined ? externalSelectedViewableBy : internalSelectedViewableBy
  const setSelectedViewableBy = externalSetSelectedViewableBy || setInternalSelectedViewableBy
  const searchMode = externalSearchMode !== undefined ? externalSearchMode : internalSearchMode
  const setSearchMode = externalSetSearchMode || setInternalSearchMode

  const [isLoading, setIsLoading] = useState(false)
  const [lastSearchTime, setLastSearchTime] = useState(0)
  const [uploaderOptions, setUploaderOptions] = useState([])

  const debouncedValue = useDebounce(value, 1000)

  // Create a debounced version of the filters to prevent rapid-fire searches
  const debouncedUploaders = useDebounce(selectedUploaders, 300)
  const debouncedFileTypes = useDebounce(selectedFileTypes, 300)
  const debouncedFileTags = useDebounce(selectedFileTags, 300)
  const dispatch = useDispatch()
  const bp = useSelector(getSelectedBp)
  const files = useSelector(selectBpFilesList)
  const fileTags = useSelector(selectFileTagsForBp)
  const fileTypes = useSelector(getFileTypes)

  const hasActiveFilters = selectedUploaders.length > 0 || selectedFileTypes.length > 0 || selectedFileTags.length > 0 || selectedViewableBy.length > 0

  // Show ViewableBy filter only when there are other active filters
  const hasOtherActiveFilters = (value && value.length >= 3) || selectedUploaders.length > 0 || selectedFileTypes.length > 0 || selectedFileTags.length > 0

  // Collapsible filters state - expanded by default if filters are active, collapsed otherwise
  const [filtersExpanded, setFiltersExpanded] = useState(() => hasActiveFilters)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  )

  // Auto-expand when filters become active
  useEffect(() => {
    if (hasActiveFilters && !filtersExpanded) {
      setFiltersExpanded(true)
    }
  }, [hasActiveFilters, filtersExpanded])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleClearValue = () => {
    setValue('')
    setIsSearching(false)

    // Clear search results in Redux
    dispatch(searchLocalFilesRoutine.success([]))

    onSearchCriteria({
      query: '',
      uploaderIds: [],
      fileTypeIds: [],
      tagIds: [],
      folderId: selectedFolder?.id || null,
      categoryId: selectedCategory?.id || null,
      isActive: false
    })
  }

  const handleClearFilters = () => {
    setSelectedUploaders([])
    setSelectedFileTypes([])
    setSelectedFileTags([])
    setSelectedViewableBy([])

    // Clear search results in Redux
    dispatch(searchLocalFilesRoutine.success([]))

    // Always clear search state when clearing filters manually
    setIsSearching(false)
  }

  const handleInputChange = e => setValue(e.target.value)

  const handleSearchModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode)
      localStorage.setItem('fileSearchMode', newMode)

      // Re-trigger search with new mode if there's an active search
      const minChars = newMode === 'content' ? 6 : 3
      const hasQuery = value && value.length >= minChars
      const hasFilters = selectedUploaders.length > 0 || selectedFileTypes.length > 0 || selectedFileTags.length > 0

      if ((hasQuery || hasFilters) && bp?.id) {
        // Force immediate re-search with new mode (bypass debounce)
        setIsLoading(true)
        dispatch(
          searchLocalFilesRoutine({
            query: value || '',
            uploaderIds: selectedUploaders,
            fileTypeIds: selectedFileTypes,
            fileTagIds: selectedFileTags,
            searchMode: newMode,
            bpId: bp.id,
            onComplete: () => setIsLoading(false)
          })
        )
      }
    }
  }

  // Refs for values that should NOT cause performSearch to re-run.
  // Folder/category clicks should never trigger a new API search.
  const selectedFolderRef = useRef(selectedFolder)
  const selectedCategoryRef = useRef(selectedCategory)
  const onSearchCriteriaRef = useRef(onSearchCriteria)
  useEffect(() => { selectedFolderRef.current = selectedFolder }, [selectedFolder])
  useEffect(() => { selectedCategoryRef.current = selectedCategory }, [selectedCategory])
  useEffect(() => { onSearchCriteriaRef.current = onSearchCriteria }, [onSearchCriteria])

  const performSearch = useCallback(() => {
    const minChars = searchMode === 'content' ? 6 : 3
    const hasQuery = debouncedValue && debouncedValue.length >= minChars
    const hasFilters =
      debouncedUploaders.length > 0 ||
      debouncedFileTypes.length > 0 ||
      debouncedFileTags.length > 0

    // Build search criteria for parent state tracking
    const searchCriteria = {
      query: debouncedValue || '',
      uploaderIds: debouncedUploaders,
      fileTypeIds: debouncedFileTypes,
      tagIds: debouncedFileTags,
      folderId: selectedFolderRef.current?.id || null,
      categoryId: selectedCategoryRef.current?.id || null,
      isActive: hasQuery || hasFilters,
      limitToCurrentFolder: !!(selectedFolderRef.current || selectedCategoryRef.current)
    }

    // Pass criteria to parent
    onSearchCriteriaRef.current(searchCriteria)

    // Call database search API (Building Passport local search only — aggregate
    // views have no single bp and run their own server-side search off the criteria above)
    if (hasQuery || hasFilters) {
      setIsLoading(true)
      setIsSearching(true)

      if (bp?.id) {
        dispatch(
          searchLocalFilesRoutine({
            query: debouncedValue || '',
            uploaderIds: debouncedUploaders,
            fileTypeIds: debouncedFileTypes,
            fileTagIds: debouncedFileTags,
            searchMode: searchMode,
            bpId: bp.id,
            ...(customViewId ? { customViewId } : {}),
            onComplete: () => setIsLoading(false)
          })
        )
      } else {
        setIsLoading(false)
      }
    } else {
      // Clear search when no query and no filters
      setIsLoading(false)
      setIsSearching(false)
      dispatch(searchLocalFilesRoutine.success([]))
    }

  }, [debouncedValue, debouncedUploaders, debouncedFileTypes, debouncedFileTags, searchMode, dispatch, bp?.id, setIsSearching])

  useEffect(() => {
    performSearch()
  }, [performSearch])

  // Load file tags when component mounts
  useEffect(() => {
    if (bp?.id) {
      dispatch(getFileTagsForBpRoutine({ bpId: bp.id }))
    }
  }, [bp?.id, dispatch])

  // Fetch uploaders for filter chip labels
  useEffect(() => {
    const fetchUploaders = async () => {
      if (!bp?.id) return
      try {
        const response = await getFileUploaders(bp.id)
        const data = response.data?.data || response.data || []
        setUploaderOptions(data)
      } catch (error) {
        console.error('Failed to fetch uploaders for filter chips:', error)
      }
    }
    fetchUploaders()
  }, [bp?.id])


  return (
    <SearchWrapper>
      {/* Search bar row */}
      <SearchBarRow>
        <SearchTopRow>
          <SearchContainer disabled={disabled}>
            <SearchInputRow>
            <SearchIconWrapper>
              {isLoading ? <StyledLoader size={20} /> : <StyledSearchIcon />}
            </SearchIconWrapper>

            <SearchInput
              {...props}
              data-tour-id='files.search'
              placeholder={searchMode === 'content'
                ? "Search file content (min 6 characters)..."
                : "Search file names (min 3 characters)..."}
              disabled={disabled}
              value={value}
              onChange={handleInputChange}
            />

              {!isEmpty(value) && (
                <ClearButton onClick={handleClearValue}>
                  <StyledClearIcon />
                </ClearButton>
              )}
            </SearchInputRow>

            <SearchControls>
              <Tooltip title="Switch between filename search and AI content search">
                <StyledToggleButtonGroup
                  data-tour-id='files.search-mode'
                  value={searchMode}
                  exclusive
                  onChange={handleSearchModeChange}
                  size="small"
                  disabled={disabled}
                >
                  <StyledToggleButton value="name">Name</StyledToggleButton>
                  <StyledToggleButton value="content">Content</StyledToggleButton>
                </StyledToggleButtonGroup>
              </Tooltip>
            </SearchControls>
          </SearchContainer>

          {isMobile && (
            <FiltersToggleRow>
              <FiltersToggleButton onClick={() => setFiltersExpanded(!filtersExpanded)}>
                <FilterListIcon />
                <span>{filtersExpanded ? 'Hide' : 'Show'} Filters</span>
                {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </FiltersToggleButton>
              {hasActiveFilters && (
                <ActiveFiltersBadge>{selectedUploaders.length + selectedFileTypes.length + selectedFileTags.length + selectedViewableBy.length}</ActiveFiltersBadge>
              )}
            </FiltersToggleRow>
          )}
        </SearchTopRow>
        {(() => {
          const minChars = searchMode === 'content' ? 6 : 3
          const remaining = value ? minChars - value.length : 0
          const needsMore = value && remaining > 0
          return (
            <SearchHelperText $loading={isLoading} $warning={needsMore}>
              {isLoading
                ? 'Searching files...'
                : needsMore
                  ? `Type ${remaining} more character${remaining > 1 ? 's' : ''} to search`
                  : searchMode === 'content'
                    ? 'AI Content Search: Minimum 6 characters required'
                    : 'Name Search: Minimum 3 characters required'}
            </SearchHelperText>
          )
        })()}
      </SearchBarRow>

      {/* Full-width filters row - Collapsible */}
      <Collapse in={isMobile ? filtersExpanded : true} timeout="auto" unmountOnExit={isMobile}>
      <FiltersRow>
        <FilterContainer>
          <FilterLabel>Uploader</FilterLabel>
          <UploaderMultiSelect
            selectedUploaders={selectedUploaders}
            setSelectedUploaders={setSelectedUploaders}
            placeholder='All uploaders'
            bpId={bp?.id}
          />
        </FilterContainer>

        <FilterContainer>
          <FilterLabel>File Type</FilterLabel>
          <FileTypeMultiSelect
            selectedFileTypes={selectedFileTypes}
            setSelectedFileTypes={setSelectedFileTypes}
            placeholder='All file types'
            files={files}
          />
        </FilterContainer>

        <FilterContainer>
          <FilterLabel>Tags</FilterLabel>
          <TagsMultiSelect
            selectedFileTags={selectedFileTags}
            setSelectedFileTags={setSelectedFileTags}
            placeholder='All tags'
            fileTags={fileTags}
          />
        </FilterContainer>

        {hasOtherActiveFilters && (
          <FilterContainer>
            <FilterLabel>Viewable By</FilterLabel>
            <ViewableBySelect
              selectedUsers={selectedViewableBy}
              setSelectedUsers={setSelectedViewableBy}
              placeholder='All users'
              bp={bp}
            />
          </FilterContainer>
        )}

        <ResetContainer>
          <ResetButton onClick={handleClearFilters} disabled={!hasActiveFilters}>
            Reset filters
          </ResetButton>
        </ResetContainer>
      </FiltersRow>
      </Collapse>

      {/* Active filter chips */}
      {(debouncedValue?.length >= 3 || hasActiveFilters) && (
        <ActiveFiltersWrapper>
          <FilterChipsLabel>Active filters:</FilterChipsLabel>
          {debouncedValue?.length >= 3 && (
            <StyledChip
              label={`Search: "${debouncedValue}"`}
              size="small"
              onDelete={() => setValue('')}
              color="primary"
              variant="filled"
            />
          )}
          {debouncedUploaders?.map(uploaderId => {
            // Find the uploader from uploaderOptions (fetched from API)
            const uploader = uploaderOptions?.find(u => u.id === uploaderId)
            const uploaderName =
              uploader?.name || uploader?.email || `User ${uploaderId.substring(0, 8)}...`

            return (
              <StyledChip
                key={`uploader-${uploaderId}`}
                label={`Uploader: ${uploaderName}`}
                size="small"
                onDelete={() => setSelectedUploaders(prev => prev.filter(id => id !== uploaderId))}
                variant="outlined"
              />
            )
          })}
          {debouncedFileTypes?.map(typeId => {
            let typeName = 'Unknown'
            if (typeId === 'null') {
              typeName = 'Uncategorised'
            } else {
              const fileType = fileTypes?.find(ft => ft.id === typeId)
              typeName = fileType?.name || 'Unknown'
            }
            return (
              <StyledChip
                key={`type-${typeId}`}
                label={`Type: ${typeName}`}
                size="small"
                onDelete={() => setSelectedFileTypes(prev => prev.filter(id => id !== typeId))}
                variant="outlined"
              />
            )
          })}
          {debouncedFileTags?.map(tagId => {
            const tag = fileTags?.find(t => t.id === tagId)
            const tagName = tag?.name || `Tag ${tagId.substring(0, 8)}...`
            return (
              <StyledChip
                key={`tag-${tagId}`}
                label={`Tag: ${tagName}`}
                size="small"
                onDelete={() => setSelectedFileTags(prev => prev.filter(id => id !== tagId))}
                variant="outlined"
              />
            )
          })}

          {((debouncedValue?.length >= 3 ? 1 : 0) +
            debouncedUploaders.length +
            debouncedFileTypes.length +
            debouncedFileTags.length) > 1 && (
            <ClearAllChip
              label="Clear All"
              size="small"
              onClick={() => {
                setValue('')
                setSelectedUploaders([])
                setSelectedFileTypes([])
                setSelectedFileTags([])
              }}
              color="error"
              variant="outlined"
            />
          )}
        </ActiveFiltersWrapper>
      )}
    </SearchWrapper>
  )
}

export default StreamlinedFilesSearch

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 8px;
  }
`

const SearchBarRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 768px) {
    gap: 4px;
  }
`

const SearchTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 8px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }
`

const SearchHelperText = styled.div`
  font-size: 12px;
  color: ${({ $loading, $warning, theme }) => $loading ? '#1a8a96' : $warning ? '#e65100' : theme.colors.grey[700]};
  font-weight: ${({ $loading, $warning }) => ($loading || $warning) ? '600' : '400'};
  padding-left: 4px;
  transition: color 0.2s ease, font-weight 0.2s ease;

  @media (max-width: 900px) {
    padding-left: 0;
    font-size: 11px;
    line-height: 1.35;
    opacity: 0.85;
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  padding: 0 8px;
  height: 48px;
  width: 100%;
  min-width: 400px;
  transition: all 0.2s ease;
  opacity: ${props => (props.disabled ? '.5' : '1')};
  box-sizing: border-box;
  max-width: 100%;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }

  @media (max-width: 900px) {
    width: 100%;
    min-width: 0;
    flex-direction: column;
    height: auto;
    padding: 8px;
    gap: 8px;
    align-items: stretch;
  }

  @media (max-width: 768px) {
    padding: 4px;
    border-radius: 6px;
    border-width: 1.5px;
    gap: 4px;
    flex: 1;
  }
`

const SearchInputRow = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 8px;

  @media (max-width: 900px) {
    width: 100%;
    gap: 6px;
  }

  @media (max-width: 768px) {
    gap: 4px;
  }
`

const SearchControls = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  flex-shrink: 0;
  min-width: fit-content;
  white-space: nowrap;

  @media (max-width: 900px) {
    margin-left: 0;
    width: 100%;
    justify-content: flex-start;
    gap: 8px;
  }

  @media (max-width: 768px) {
    gap: 4px;
  }
`

const FiltersRow = styled.div`
  display: flex;
  align-items: end;
  gap: 16px;
  width: 100%;

  @media (max-width: 1100px) {
    flex-wrap: wrap;
    gap: 12px;
  }

  @media (max-width: 900px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    align-items: start;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

const FilterContainer = styled.div`
  flex: 1;
  min-width: 200px;

  @media (max-width: 1100px) {
    min-width: 180px;
  }

  @media (max-width: 900px) {
    min-width: 0;
    width: 100%;
  }
`

const ResetContainer = styled.div`
  flex-shrink: 0;
  min-width: fit-content;

  @media (max-width: 900px) {
    width: 100%;
    grid-column: 1 / -1;
  }
`

const SearchIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
  flex-shrink: 0;

  @media (max-width: 900px) {
    margin-right: 6px;
  }

  @media (max-width: 768px) {
    margin-right: 4px;
  }
`

const StyledSearchIcon = styled(SearchIcon)`
  font-size: 20px !important;

  @media (max-width: 768px) {
    font-size: 18px !important;
  }
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  background: transparent;
  color: ${({ theme }) => theme.colors.haiti};
  min-width: 0;
  width: 0;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey[600]};
    font-size: 15px;
  }

  @media (max-width: 900px) {
    flex: 1;
    min-width: 0;
    font-size: 15px;
    padding: 4px 0;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 2px 0;
    min-width: 120px;

    &::placeholder {
      font-size: 13px;
    }
  }
`

const FilterLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;

  @media (max-width: 900px) {
    font-size: 11px;
    margin-bottom: 4px;
  }
`

const ResetButton = styled.button`
  background-color: ${({ theme, disabled }) =>
    disabled ? 'transparent' : theme.colors.error};
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors.grey[600] : theme.colors.white};
  border: 2px solid ${({ theme, disabled }) =>
    disabled ? theme.colors.grey[600] : theme.colors.error};
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  white-space: nowrap;
  height: 42px;
  opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.errorDark || '#b71c1c'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(210, 71, 36, 0.3);
  }

  @media (max-width: 900px) {
    width: 100%;
    height: 44px;
    padding: 10px 14px;
  }
`

const ClearButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.grey[100]};
  }
`

const StyledClearIcon = styled(ClearIcon)`
  font-size: 18px !important;
  color: ${({ theme }) => theme.colors.grey[600]};
`

const StyledLoader = styled(CircularProgress)`
  color: ${({ theme }) => theme.colors.primary};
`

const ActiveFiltersWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.lightGrey}10;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};

  @media (max-width: 900px) {
    padding: 10px;
    gap: 6px;
  }
`

const FilterChipsLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 4px;

  @media (max-width: 900px) {
    width: 100%;
    margin-right: 0;
    font-size: 11px;
    opacity: 0.8;
  }
`

const StyledChip = styled(Chip)`
  && {
    height: 26px;
    font-size: 12px;
    max-width: none;

    .MuiChip-label {
      padding-left: 8px;
      padding-right: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 52vw;
    }

    .MuiChip-deleteIcon {
      font-size: 16px;
    }

    @media (max-width: 900px) {
      .MuiChip-label {
        max-width: 60vw;
      }
    }
  }
`

const ClearAllChip = styled(Chip)`
  && {
    height: 26px;
    font-size: 12px;
    font-weight: 600;
    margin-left: 8px;

    &:hover {
      background-color: ${({ theme }) => theme.colors.error}10;
    }

    @media (max-width: 900px) {
      margin-left: 0;
    }
  }
`

const FiltersToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 6px;
    width: 100%;
  }
`

const FiltersToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}08;
  }

  svg {
    font-size: 18px;
  }

  @media (max-width: 900px) {
    padding: 10px 14px;
    font-size: 13px;
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
    gap: 6px;
    border-radius: 6px;
    border-width: 1.5px;
    width: 100%;
    height: 36px;
    justify-content: flex-start;

    svg {
      font-size: 16px;
    }
  }
`

const ActiveFiltersBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  && {
    height: 32px;
    border-radius: 6px;
    background: ${({ theme }) => theme.colors.white};

    @media (max-width: 900px) {
      height: 30px;
    }
  }
`

const StyledToggleButton = styled(ToggleButton)`
  && {
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: none;
    border: 1px solid ${({ theme }) => theme.colors.grey[300]};
    color: ${({ theme }) => theme.colors.grey[700]};
    transition: all 0.2s ease;

    &.Mui-selected {
      background-color: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.white};
      border-color: ${({ theme }) => theme.colors.primary};

      &:hover {
        background-color: ${({ theme }) => theme.colors.primaryDark || theme.colors.primary};
      }
    }

    @media (max-width: 768px) {
      padding: 2px 8px;
      font-size: 11px;
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.grey[100]};
    }

    &:not(:last-child) {
      border-right: 1px solid ${({ theme }) => theme.colors.grey[300]};
    }

    @media (max-width: 900px) {
      padding: 3px 10px;
      font-size: 11px;
    }
  }
`
